import { useRef, useEffect } from "react";
import { useStore } from "zustand";
import { useHover, usePress } from "@react-aria/interactions";
import { mergeProps } from "@react-aria/utils";

import {
  Image,
  Badge,
  ActionButtonGroup,
  ActionButton,
} from "@react-spectrum/s2";

import DeleteIcon from "@react-spectrum/s2/icons/Delete";
import DownloadIcon from "@react-spectrum/s2/icons/Download";

import { store } from "../../store";
import { downloadImage } from "@app/services/downloadFile";
import { useVisualUrl } from "@app/hooks/useAvatarUrl";

import {
  style,
  lightDark,
} from "@react-spectrum/s2/style" with { type: "macro" };

export const StoryCardVisual = (props) => {
  const ref = useRef(null);
  const { hoverProps, isHovered } = useHover({});
  const { pressProps, isPressed } = usePress({
    onPress: props?.onPress ?? (() => {}),
  });

  const colorScheme = useStore(store, (state) => state.colorScheme);
  const isDark = colorScheme === "dark";

  const isSelected = props?.item?.visual === props?.idx;

  // Get the visual URL
  const visualUrl = useVisualUrl(props?.visual);

  useEffect(() => {
    if (isSelected && ref.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [isSelected]);

  return (
    <div
      ref={ref}
      {...mergeProps(hoverProps, pressProps)}
      className={style({
        boxSizing: "border-box",
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: "100%",
        display: "flex",
        scrollSnapAlign: "center",
        scrollSnapStop: "always",
        padding: 24,
        transition: "transform",
        willChange: "transform",
        transform: {
          default: "none",
          isPressed: "perspective(28.125vw) translate3d(0, 0, -2px)",
        },
      })({ isPressed })}
    >
      <div
        className={style({
          position: "relative",
          size: "full",
          borderRadius: "lg",
          overflow: "hidden",
          boxShadow: {
            default: "none",
            isHovered: "emphasized",
            isSelected: "elevated",
          },
          outlineColor: {
            default: "transparent",
            isHovered: lightDark(
              "transparent-white-300",
              "transparent-black-300",
            ),
            isSelected: "gray-900",
          },
          outlineOffset: -2,
          outlineStyle: "solid",
          outlineWidth: 2,
          transition: "all",
        })({ isSelected, isHovered })}
      >
        <Image
          src={visualUrl}
          alt={props?.visual?.prompt ?? props?.item?.title}
          styles={style({
            size: "full",
            objectFit: "cover",
            position: "absolute",
            inset: 2,
            zIndex: -1,
          })}
        />
        <Badge
          variant="neutral"
          fillStyle="subtle"
          size="S"
          styles={style({
            position: "absolute",
            bottom: 8,
            insetStart: 8,
          })}
        >
          {props.idx + 1}/{props?.item?.visuals?.length}
        </Badge>
        <ActionButtonGroup
          styles={style({
            position: "absolute",
            top: 8,
            insetEnd: 8,
          })}
          staticColor={isDark ? "black" : "white"}
        >
          <ActionButton
            aria-label="Delete visual"
            onPress={props?.onDelete}
            isQuiet
          >
            <DeleteIcon />
          </ActionButton>
          <ActionButton
            aria-label="Download visual"
            onPress={() =>
              downloadImage(
                props.visual,
                props?.visual?.prompt?.trim()?.replace(/[^\w\d]+/g, "+"),
              )
            }
          >
            <DownloadIcon />
          </ActionButton>
        </ActionButtonGroup>
      </div>
    </div>
  );
};
